import ErrorMessage from "components/error-message";
import { Button } from "@material-ui/core";
import { ChangeEvent, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router";
import { RootState, StoreError, users } from "store";
import { getDisplayNameOrEmail } from "store/reducers/users/utils";
import { User } from "store/reducers/users/reducer";
import TasterList from "../../components/taster-list";
import {
    CircularProgress,
    Container,
    CountHint,
    EmptyMessage,
    ListWrap,
    SearchBar,
    SearchField,
} from './Tasters.styles';

export type TastersParams = {
    id: string
}

const Tasters = () => {
    const dispatch = useDispatch();
    const { id } = useParams<TastersParams>();
    const [query, setQuery] = useState("");
    const tasters = useSelector<RootState, string[] | undefined>(
        s => {
            const event = s?.events?.items?.find(i => i.id === id);
            const tasters = event?.tasters ?? [];
            const users = s?.users?.itemsById?.[id]?.filter(u => tasters.includes(u.id));
            return users?.map(u => getDisplayNameOrEmail(u.displayName, u.email));
        }
    );
    const championDisplayName = useSelector<RootState, string | null>(
        s => {
            const event = s?.events?.items?.find(i => i.id === id);
            const tasters = event?.tasters ?? [];
            const champion = s?.users?.itemsById?.[id]?.find(
                u => u.hasChampionBelt && tasters.includes(u.id)
            );
            return champion
                ? getDisplayNameOrEmail(champion.displayName, champion.email)
                : null;
        }
    );
    const loading = useSelector<RootState, boolean | undefined>(
        s => s?.events?.itemsLoading[id] || s?.users?.loadingFor?.[id]
    );
    const eventUsers = useSelector<RootState, User[] | undefined>(
        s => s?.users?.itemsById?.[id]
    );
    const usersError = useSelector<RootState, StoreError | null>(
        s => s?.users?.errorFor?.[id] ?? null
    );
    const eventError = useSelector<RootState, StoreError | undefined>(
        s => s?.events?.itemsError[id]
    );
    const error = eventError || usersError;
    const displayLoading = loading && !eventUsers;

    useEffect(() => {
        dispatch(users.load(id));
    }, [id, dispatch]);

    const filtered = (tasters ?? []).filter(name =>
        name.toLowerCase().includes(query.toLowerCase())
    );

    const handleQueryChange = (e: ChangeEvent<HTMLInputElement>) => {
        setQuery(e.target.value);
    };

    return (
        <Container id="tasters">
            {displayLoading ?
                <CircularProgress />
                :
                <>
                    {error && (
                        <ErrorMessage error={error}>
                            {usersError && (
                                <Button color="inherit" onClick={() => dispatch(users.load(id))}>
                                    Retry
                                </Button>
                            )}
                        </ErrorMessage>
                    )}
                    <SearchBar>
                        <SearchField value={query} onChange={handleQueryChange} />
                    </SearchBar>
                    <CountHint>
                        {filtered.length} taster{filtered.length === 1 ? "" : "s"}
                    </CountHint>
                    <ListWrap>
                        {filtered.length === 0 ? (
                            <EmptyMessage>
                                {query
                                    ? `No tasters match "${query}".`
                                    : "No tasters added"}
                            </EmptyMessage>
                        ) : (
                            <TasterList
                                tasters={filtered}
                                isTasterChampion={name =>
                                    !!championDisplayName && name === championDisplayName
                                }
                            />
                        )}
                    </ListWrap>
                </>
            }
        </Container>
    );
}
export default Tasters;
